import type React from "react";
import { useRef } from "react";
import { uploadFile } from "../../utils/files";

/** A document that has been uploaded to Flow file storage. */
export interface UploadedDocument {
  fileId: string;
  filename: string;
  attachmentKey: string;
  mimetype?: string;
  [k: string]: unknown;
}

/**
 * A document selected by the caseworker. It holds the raw {@link File} until the
 * task is submitted; entries loaded from saved task data already carry a
 * `fileId` instead.
 */
export interface PendingDocument {
  /** The raw file, present until it is uploaded on submit. */
  file?: File;
  /** Set once the file has been uploaded to Flow file storage. */
  fileId?: string;
  filename: string;
  attachmentKey?: string;
  mimetype?: string;
  [k: string]: unknown;
}

const DEFAULT_ACCEPT = ".jpg,.jpeg,.png,.pdf,.doc,.docx,.json";

// The fileId is included so two files with the same name get distinct
// attachment keys (otherwise the later upload would overwrite the earlier one).
const toAttachmentKey = (filename: string, fileId: string): string =>
  `${filename.trim()}-${fileId}`.replace(/[^A-Za-z0-9_-]+/g, "-");

/**
 * Uploads any not-yet-uploaded documents to Flow file storage and returns the
 * fully resolved list. Documents that already have a `fileId` are passed through
 * unchanged (minus the raw file handle).
 *
 * Call this from a task's submit handler so the selected files only reach
 * storage when the caseworker actually completes/saves the task — nothing is
 * uploaded while they are still picking files.
 */
export async function uploadPendingDocuments(
  documents: PendingDocument[],
): Promise<UploadedDocument[]> {
  const pendingFiles = documents
    .filter((d) => d.file && !d.fileId)
    .map((d) => d.file as File);

  let uploaded: { fileId: string; filename: string }[] = [];
  if (pendingFiles.length > 0) {
    const { uploadedFilesResponse } = await uploadFile(pendingFiles);
    uploaded = uploadedFilesResponse ?? [];
  }

  let next = 0;
  return documents.map((doc) => {
    if (doc.file && !doc.fileId) {
      const response = uploaded[next++];
      const filename = response?.filename ?? doc.filename;
      return {
        fileId: response?.fileId,
        filename,
        attachmentKey:
          doc.attachmentKey ?? toAttachmentKey(filename, response?.fileId ?? ""),
        mimetype: doc.mimetype,
      } as UploadedDocument;
    }
    // Already uploaded — drop the (absent) file handle and normalise the key.
    const { file, ...rest } = doc;
    return {
      ...rest,
      attachmentKey:
        doc.attachmentKey ?? toAttachmentKey(doc.filename, doc.fileId ?? ""),
    } as UploadedDocument;
  });
}

interface DocumentUploadProps {
  /** The currently selected documents (controlled). */
  value: PendingDocument[];
  /** Called with the next list of documents whenever files are added or removed. */
  onChange: (documents: PendingDocument[]) => void;
  /** Heading shown above the upload button. Pass an empty string to hide it. */
  label?: string;
  /** Comma-separated list of accepted file extensions. */
  accept?: string;
  /** Disables selecting and removing files. */
  disabled?: boolean;
}

/**
 * Reusable caseworker document picker.
 *
 * Collects a controlled list of {@link PendingDocument}s but does NOT upload
 * anything on selection — the raw files are held until the parent submits the
 * task and calls {@link uploadPendingDocuments}. Rendering, the upload button
 * and the file list are handled internally, so task views only wire
 * `value`/`onChange`.
 *
 * @component
 * @param props - The component props (see {@link DocumentUploadProps}).
 * @returns The rendered component.
 */
const DocumentUpload: React.FC<DocumentUploadProps> = ({
  value,
  onChange,
  label = "Documentation",
  accept = DEFAULT_ACCEPT,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFilesSelected = (files: File[]) => {
    if (files.length === 0) {
      return;
    }
    const additions: PendingDocument[] = files.map((file) => ({
      file,
      filename: file.name,
      mimetype: file.type || undefined,
    }));
    onChange([...value, ...additions]);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div style={{ padding: "0 28px" }}>
      {label && <div style={{ fontWeight: 600, marginBottom: 8 }}>{label}</div>}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          const files = e.target.files ? Array.from(e.target.files) : [];
          handleFilesSelected(files);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => {
          if (!disabled) {
            fileInputRef.current?.click();
          }
        }}
        disabled={disabled}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 16px",
          borderRadius: 8,
          border: "2px solid #1b4df5",
          background: "transparent",
          color: "#1b4df5",
          fontWeight: 600,
          cursor: disabled ? "not-allowed" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
        Upload document
      </button>
      {value.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, marginTop: 8 }}>
          {value.map((doc, index) => (
            <li
              key={doc.fileId ?? `${doc.filename}-${index}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "4px 0",
              }}
            >
              <span>{doc.filename}</span>
              {/* Only files not yet uploaded can be removed; already committed
                  documents (loaded from saved state) cannot be deleted here. */}
              {!doc.fileId && (
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  disabled={disabled}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#E4251B",
                    cursor: disabled ? "not-allowed" : "pointer",
                  }}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DocumentUpload;
